import React, { useState, useRef, useImperativeHandle, forwardRef, ClipboardEvent, ChangeEvent } from 'react'
import { Link, Stack, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { toast } from 'components/global/CUK/Toast/Toast'

import useActions, { cleanTwoFaStorage } from '../../../use-actions'
import { getGraphQLErrorCode } from '../LoginPage/errorHandler'
import { ApolloError } from '@apollo/client'

const LOGIN_REQUIRED_SEND_CODE_ERRORS = ['TWO_FA_TOKEN', 'TWO_FA_ATTEMPT']

const SEND_CODE_ERROR_MESSAGES: Record<string, string> = {
  TOO_MANY_REQUESTS: 'login.error.too_many_requests'
}

const LoginAgain: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const handleLoginAgain = (): void => {
    cleanTwoFaStorage()
    navigate('/auth/login')
  }

  return (
    <Link
      component={'button'}
      type={'button'}
      sx={{ fontSize: 14 }}
      aria-label={t('login.sucess_new_message')}
      onClick={handleLoginAgain}
    >
      {t('login.sucess_new_message')}
    </Link>
  )
}

const SendCode: React.FC<{onLoginRequired: () => void}> = ({onLoginRequired}) => {
  const [loading, setLoading] = useState<boolean>(false)

  const {twoFaSendCode} = useActions()
  const theme = useTheme()
  const {t} = useTranslation()

  const handleSendCode = async (): Promise<void> => {
    setLoading(true)
    const {errors} = await twoFaSendCode()
    if (errors) {
      const errorCode = getGraphQLErrorCode(errors as ApolloError) as string
      if (LOGIN_REQUIRED_SEND_CODE_ERRORS.includes(errorCode)) {
        onLoginRequired()
      } else {
        toast.error(t(SEND_CODE_ERROR_MESSAGES[errorCode] ?? 'ui_elements.an_error_has_occurred'))
      }
    } else {
      toast.success(t('two_fa_send_code.toast_resend'))
    }
    setLoading(false)
  }
  return (
    <Stack direction={'row'} gap={.5} alignItems={'center'}>
      <Typography sx={{fontSize: 14}} component={'span'} dangerouslySetInnerHTML={{ __html: t('two_fa_code_resend.part_1')}} />
      <Typography
        sx={{
          fontSize: 14,
          color: loading ? theme.palette.success.main : theme.palette.primary.main,
          cursor: 'pointer'}}
        component={'span'}
        onClick={async () => {
          if (!loading) {
            await handleSendCode()
          }
        }}
      >
        {
          loading
            ? t('ui_elements.in_progress')
            : t('two_fa_code_resend.part_2')
        }
      </Typography>
    </Stack>
  )
}

interface DigitsProps {
  disabled: boolean
  onSubmit: (code: string) => void
}
const Digits = forwardRef<{reset: () => void}, DigitsProps>(({disabled, onSubmit}, ref) => {
  const inputsRef = useRef<HTMLInputElement[]>([])
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', ''])
  useImperativeHandle(ref, () => {
    return {
      reset: () => setDigits(['', '', '', '', '', '']),
    }
  })

  const handlePaste = async (e: ClipboardEvent<HTMLDivElement>): Promise<void> => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text/plain')
      .split('')
      .slice(0, digits.length)
    setDigits(pasted)
    if (pasted.every(digit => digit.length)) {
      onSubmit(pasted.join(''))
    }
  }

  const handleUpdate =  (index: number) => {
    return async (event: ChangeEvent<HTMLInputElement>) => {
      const newValues = [
        ...digits.slice(0, index),
        event.target.value,
        ...digits.slice(index + 1)
      ]
      if (newValues.every(digit => digit.length)) {
        onSubmit(newValues.join(''))
      }
      if (event.target.value && index < 5) {
        inputsRef.current[index + 1]?.focus()
      }
      setDigits(newValues)
    }
  }

  return (
    <Stack gap={1.5} direction={'row'}>
      {
        digits.map((digit, i) => (
          <input
            key={i}
            ref={ref => ref && inputsRef.current.push(ref)}
            value={digit}
            disabled={disabled}
            onPaste={handlePaste}
            onInput={handleUpdate(i)}
            maxLength={1}
            style={{
              width: 40,
              height: 48,
              textAlign: 'center',
              border: '1px solid #E0E0E0',
              borderRadius: 4
            }}
          />
        ))
      }
    </Stack>
  )
})

const ValidationCode: React.FC<{onValid: () => void}> = ({onValid}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoginRequired, setIsLoginRequired] = useState<boolean>(false)
  const digitsRef = useRef<{reset: () => void} | null>(null)
  const { twoFaActivate, twoFaSession } = useActions()
  const { t } = useTranslation()
  const theme = useTheme()
  const hasUserId = Boolean(localStorage.getItem('TWO_FA_USER_ID')?.length)
  const hasToken = Boolean(localStorage.getItem('TWO_FA_TOKEN')?.length)
  const isLoginFlow = localStorage.getItem('TWO_FA_ACTIVATED') === '1'

  const handleLoginRequired = (): void => {
    setIsLoginRequired(true)
    setError(t('errors.two_fa.login_again'))
  }

  const handleValid = async (code: string) => {
    setLoading(true)
    const { errors }: {errors?: ApolloError | unknown} = isLoginFlow
      ? await twoFaSession(code)
      : await twoFaActivate(code)

    if (errors) {
      const errorCode = getGraphQLErrorCode(errors as ApolloError) as string
      if (errorCode === 'TWO_FA_ATTEMPT' && isLoginFlow) {
        handleLoginRequired()
      } else if (errorCode === 'TWO_FA_TOKEN') {
        setError(t('errors.two_fa.token'))
      } else if (errorCode === 'TWO_FA_ATTEMPT') {
        setError(t('errors.two_fa.attempt'))
      } else if (errorCode === 'TWO_FA_CODE') {
        setError(t('errors.two_fa.code'))
      } else if (errorCode === 'TWO_FA_EXPIRATION') {
        setError(t('errors.two_fa.expiration'))
      } else if (errorCode === 'USER_LOCKED') {
        setError(t('login.error.password_locked'))
      } else if (errorCode === 'TOO_MANY_REQUESTS') {
        setError(t('login.error.too_many_requests'))
      } else {
        setError(t('ui_elements.an_error_has_occurred'))
      }
      setTimeout(() => digitsRef.current?.reset(), 2000)
    } else {
      onValid()
    }
    setLoading(false)
  }

  const renderResendSection = (): React.ReactNode => {
    if (isLoginRequired) {
      return <LoginAgain />
    }
    if (!hasUserId) {
      return <Typography component={'span'} color={'error'} sx={{fontSize: 10, fontWeight: '500'}}>{t('two_fa_errors.user_id')}</Typography>
    }
    return <SendCode onLoginRequired={handleLoginRequired} />
  }

  return (
    <Stack gap={2} sx={{ alignItems: 'center', maxWidth: '400px', color: '#656565' }}>
      <Typography
        component={'h4'}
        sx={{
          fontSize: 24,
          fontWeight: '600'
        }}
      >
        {t('two_fa_disclaimer.title')}
      </Typography>
      <Stack direction={'row'} gap={1} textAlign={'center'}>
        <Typography sx={{fontSize: 14}} component={'span'} dangerouslySetInnerHTML={{ __html: t('two_fa_code_sent.title', {email: localStorage.getItem('TWO_FA_USER_EMAIL')})}} />
      </Stack>
      {
        hasToken ? (
          <Digits
            ref={digitsRef}
            disabled={loading || isLoginRequired}
            onSubmit={handleValid}
          />
        ) : <Typography component={'span'} color={'error'} sx={{fontSize: 10, fontWeight: '500'}}>{t('two_fa_errors.token')}</Typography>
      }

      {(Boolean(error) || loading) && (
        <Typography component={'span'} color={loading ? theme.palette.success.main : 'error'} sx={{fontSize: 10, fontWeight: '500'}}>{loading ? t('ui_elements.sending') : error}</Typography>
      )}
      {renderResendSection()}
    </Stack>
  )
}

export default ValidationCode